import { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Loader2, Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { aurora, auroraPalette } from '@/components/aurora/tokens';
import { usePullToRefresh } from '@/components/mobile/crm/usePullToRefresh';

export interface MobileConversationItem {
  id: string;
  title: string;
  /**
   * Anteprima dell'ultimo messaggio (testo già normalizzato dal chiamante:
   * niente HTML, niente markdown). Può essere `null` per nuove
   * conversazioni vuote.
   */
  lastMessage?: string | null;
  /** ISO timestamp dell'ultimo messaggio, mostrato come ora/data relativa. */
  lastMessageAt?: string | null;
  /** Numero di messaggi non letti. 0 → nessun badge. */
  unread?: number;
  /** URL avatar; se mancante viene generato un fallback con iniziali. */
  avatarUrl?: string | null;
  /** Sottotitolo opzionale (es. presenza, ruolo, "ha scritto…"). */
  subtitle?: string | null;
  /** Stato di presenza per il dot online (solo DM). */
  presence?: 'online' | 'away' | 'offline';
  /** Pin a inizio lista (sticky). */
  pinned?: boolean;
  /** Mute → badge silenziato. */
  muted?: boolean;
}

interface MobileConversationListProps {
  items: MobileConversationItem[];
  loading?: boolean;
  /** Item selezionato (visibile solo in layout master-detail desktop-wide). */
  activeId?: string | null;
  onSelect: (item: MobileConversationItem) => void;
  /** Hook pull-to-refresh. Se assente la gesture è disabilitata. */
  onRefresh?: () => Promise<void> | void;
  /** Mostra/Nascondi la search bar nello header. Default: true. */
  searchable?: boolean;
  /** Placeholder della search. */
  searchPlaceholder?: string;
  /** Titolo opzionale (renderizzato sopra la lista, non sticky). */
  heading?: string;
  /** Stato vuoto custom. Default: messaggio neutro Aurora. */
  emptyState?: React.ReactNode;
  className?: string;
}

function formatRelative(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = Date.now();
  const diffMs = now - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'ora';
  if (min < 60) return `${min}m`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}g`;
  try {
    return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit' }).format(d);
  } catch {
    return '';
  }
}

function initialsFor(title: string): string {
  const parts = title.trim().split(/\s+/).slice(0, 2);
  return parts
    .map((p) => p.charAt(0).toUpperCase())
    .join('')
    .padEnd(1, '·');
}

export default function MobileConversationList({
  items,
  loading,
  activeId,
  onSelect,
  onRefresh,
  searchable = true,
  searchPlaceholder = 'Cerca conversazione…',
  heading,
  emptyState,
  className,
}: MobileConversationListProps) {
  const [query, setQuery] = useState('');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pullState = usePullToRefresh(scrollerRef, {
    onRefresh: onRefresh ?? (() => Promise.resolve()),
    enabled: !!onRefresh,
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...items].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
    if (!q) return sorted;
    return sorted.filter((i) =>
      [i.title, i.lastMessage, i.subtitle].filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [items, query]);

  const pullTranslate = pullState.refreshing ? 44 : pullState.pullDistance;

  return (
    <div
      className={cn('flex flex-col h-full bg-background', className)}
      data-testid="mobile-conversation-list"
    >
      {(searchable || heading) && (
        <div
          className="sticky top-0 z-10 bg-background/85 backdrop-blur-md border-b border-border"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          {heading && (
            <div className="px-4 pt-3 pb-1">
              <h2 className={cn(aurora.type.headline, 'text-foreground')}>{heading}</h2>
            </div>
          )}
          {searchable && (
            <div className="px-3 py-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="h-10 pl-9 rounded-full bg-muted/60 border-transparent focus-visible:border-border"
                  data-testid="mobile-conversation-list-search"
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto overscroll-contain"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="mobile-conversation-list-scroller"
      >
        {/* Pull-to-refresh indicator */}
        {(pullState.pulling || pullState.refreshing) && (
          <div
            className="flex items-center justify-center text-muted-foreground"
            style={{ height: pullTranslate }}
            aria-hidden
          >
            <Loader2
              className={cn('w-4 h-4', pullState.refreshing && 'animate-spin')}
              style={{ color: auroraPalette.indigo }}
            />
          </div>
        )}

        {loading && items.length === 0 ? (
          <div className="p-3 space-y-2" data-testid="mobile-conversation-list-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-2">
                <div className="h-11 w-11 rounded-full bg-muted animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-1/3 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-2/3 bg-muted/70 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-center px-8 py-16 text-muted-foreground"
            data-testid="mobile-conversation-list-empty"
          >
            {emptyState ?? (
              <>
                <Inbox className="w-10 h-10 mb-3 opacity-50" />
                <p className={cn(aurora.type.body, 'max-w-[260px]')}>
                  {query ? 'Nessuna conversazione corrisponde alla ricerca.' : 'Nessuna conversazione ancora.'}
                </p>
              </>
            )}
          </div>
        ) : (
          <ul role="listbox" aria-label="Conversazioni">
            {filtered.map((item, index) => {
              const isActive = item.id === activeId;
              const unread = item.unread ?? 0;
              const showBadge = unread > 0 && !item.muted;
              const dotMuted = unread > 0 && item.muted;
              return (
                <motion.li
                  key={item.id}
                  role="option"
                  aria-selected={isActive}
                  initial={aurora.listItemIn.initial}
                  animate={aurora.listItemIn.animate}
                  transition={{ ...aurora.listItemIn.transition, delay: aurora.stagger(index) }}
                  className={cn(
                    'border-b border-border/60 last:border-b-0',
                    isActive && 'bg-muted/50',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(item)}
                    className="w-full flex items-center gap-3 px-3 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                    style={{ outlineColor: auroraPalette.indigo }}
                    data-testid={`mobile-conversation-item-${item.id}`}
                  >
                    <div className="relative shrink-0">
                      {item.avatarUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element -- raw img is fine here
                        <img
                          src={item.avatarUrl}
                          alt=""
                          className="h-11 w-11 rounded-full object-cover bg-muted"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="h-11 w-11 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                          style={{
                            background: `linear-gradient(135deg, ${auroraPalette.indigo}, ${auroraPalette.violet})`,
                          }}
                          aria-hidden
                        >
                          {initialsFor(item.title)}
                        </div>
                      )}
                      {item.presence && item.presence !== 'offline' && (
                        <span
                          aria-hidden
                          className={cn(
                            'absolute bottom-0 right-0 h-3 w-3 rounded-full ring-2 ring-background',
                            item.presence === 'online' ? 'bg-emerald-500' : 'bg-amber-400',
                          )}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span
                          className={cn(
                            'truncate text-sm',
                            unread > 0 ? 'font-semibold text-foreground' : 'font-medium text-foreground',
                          )}
                        >
                          {item.pinned && <span aria-hidden className="mr-1 text-muted-foreground">📌</span>}
                          {item.title}
                        </span>
                        <span
                          className={cn(
                            aurora.type.caption,
                            unread > 0 ? 'text-foreground/80 font-medium' : 'text-muted-foreground',
                          )}
                        >
                          {formatRelative(item.lastMessageAt)}
                        </span>
                      </div>
                      <div className="flex items-end justify-between gap-2 mt-0.5">
                        <span
                          className={cn(
                            'truncate text-xs',
                            unread > 0 ? 'text-foreground/80' : 'text-muted-foreground',
                          )}
                        >
                          {item.subtitle ?? item.lastMessage ?? 'Nessun messaggio'}
                        </span>
                        {showBadge && (
                          <span
                            className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold inline-flex items-center justify-center text-white"
                            style={{ background: auroraPalette.indigo }}
                            aria-label={`${unread} non letti`}
                          >
                            {unread > 99 ? '99+' : unread}
                          </span>
                        )}
                        {dotMuted && (
                          <span
                            aria-label={`${unread} non letti (silenziati)`}
                            className="ml-2 h-2 w-2 rounded-full bg-muted-foreground/60"
                          />
                        )}
                      </div>
                    </div>
                  </button>
                </motion.li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
